import {
  type BrowserFamily,
  type ClientErrorAttribution,
  classifyClientErrorAttribution,
} from "@shared/client-error-telemetry";

export interface ClientRuntimeDiagnostics {
  clientBuildId?: string;
  browserFamily?: BrowserFamily;
  browserMajor?: number;
  supportsArrayAt: boolean;
  supportsStringAt: boolean;
}

type ScriptLike = { src?: unknown; type?: unknown };
type DocumentLike = { scripts?: { length: number; [index: number]: ScriptLike } };

/**
 * The entry module is the only client release identity available to an old
 * tab. Do not use /api/app-version here: it describes the currently running
 * server, not the bundle that produced this incident.
 */
export function getClientBuildId(documentLike?: DocumentLike): string | undefined {
  const doc =
    documentLike ??
    (typeof document !== "undefined" ? (document as unknown as DocumentLike) : undefined);
  if (!doc?.scripts) return undefined;
  for (let i = 0; i < doc.scripts.length; i += 1) {
    const script = doc.scripts[i];
    if (script?.type !== "module" || typeof script.src !== "string") continue;
    let pathname = script.src;
    try {
      pathname = new URL(script.src, "http://telemetry.invalid").pathname;
    } catch {
      pathname = script.src.split(/[?#]/, 1)[0];
    }
    const filename = pathname.split("/").pop() || "";
    // Vite entry output is commonly index-<hash>.js or main.<hash>.js.
    const match = filename.match(/^.+[-_.]([A-Za-z0-9_-]{6,64})\.js$/);
    if (match) return match[1];
  }
  return undefined;
}

export function browserFamilyAndMajor(userAgent?: unknown): {
  browserFamily?: BrowserFamily;
  browserMajor?: number;
} {
  const ua =
    typeof userAgent === "string"
      ? userAgent
      : typeof navigator !== "undefined" && typeof navigator.userAgent === "string"
        ? navigator.userAgent
        : "";
  // Order matters: Edge/Opera/Samsung include Chrome tokens.
  const patterns: Array<[BrowserFamily, RegExp]> = [
    ["edge", /(?:Edg|Edge)\/(\d+)/i],
    ["opera", /(?:OPR|Opera)\/(\d+)/i],
    ["samsung", /SamsungBrowser\/(\d+)/i],
    ["android-webview", /; wv\).*Version\/(\d+)/i],
    ["firefox", /(?:Firefox|FxiOS)\/(\d+)/i],
    ["safari", /Version\/(\d+).*(?:Safari|Mobile)/i],
    ["chrome", /(?:Chrome|CriOS)\/(\d+)/i],
  ];
  for (const [family, pattern] of patterns) {
    const match = ua.match(pattern);
    if (match) {
      const major = Number(match[1]);
      return Number.isInteger(major) && major >= 0 && major <= 999
        ? { browserFamily: family, browserMajor: major }
        : { browserFamily: family };
    }
  }
  return { browserFamily: "other" };
}

export function getClientRuntimeDiagnostics(): ClientRuntimeDiagnostics {
  const browser = browserFamilyAndMajor();
  return {
    ...browser,
    clientBuildId: getClientBuildId(),
    supportsArrayAt: typeof Array.prototype.at === "function",
    supportsStringAt: typeof String.prototype.at === "function",
  };
}

export function inferClientErrorAttribution(
  stack: unknown,
  componentStack: unknown,
): ClientErrorAttribution {
  return classifyClientErrorAttribution({ stack, componentStack });
}
